import { widgetApi } from "../services/api";

/**
 * Semantic name normalization — `POST /widget/parse/name`.
 *
 * Assist-only: any non-200 resolves `null` so the flow degrades to the
 * client-side positional fallback.
 */

/** Structured name — mirrors the backend's `CanonicalName`. */
export interface CanonicalName {
  firstName: string;
  lastName: string;
  middleName?: string;
  /** Maternal surname (LatAm second last name). */
  secondLastName?: string;
}

interface ParseNameApiResponse {
  success: boolean;
  data: {
    name: CanonicalName;
    cached: boolean;
    source: "llm" | "heuristic";
    model?: string;
  };
}

/**
 * Parses a free-text full name into a `CanonicalName`. Resolves `null` on
 * any failure (422, 429, network).
 */
export const parseRecipientName = async (
  fullNameText: string
): Promise<CanonicalName | null> => {
  try {
    const { data } = await widgetApi.post<ParseNameApiResponse>(
      "/widget/parse/name",
      { text: fullNameText }
    );
    return data.data.name ?? null;
  } catch {
    return null;
  }
};

/**
 * Conservative client-side fallback when the parse endpoint is unavailable.
 * Positional LatAm split over whitespace tokens:
 *
 *   2 tokens → first + last
 *   3 tokens → first + last + secondLast
 *   4+ tokens → first + middle(s) + last + secondLast
 */
export const fallbackCanonicalName = (
  fullNameText: string
): CanonicalName | null => {
  const tokens = fullNameText.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return null;
  if (tokens.length === 2) {
    return { firstName: tokens[0], lastName: tokens[1] };
  }
  if (tokens.length === 3) {
    return {
      firstName: tokens[0],
      lastName: tokens[1],
      secondLastName: tokens[2],
    };
  }
  return {
    firstName: tokens[0],
    middleName: tokens.slice(1, -2).join(" "),
    lastName: tokens[tokens.length - 2],
    secondLastName: tokens[tokens.length - 1],
  };
};
